import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text, useTheme, Checkbox, RadioButton } from 'react-native-paper';
import { Input, Button as DSButton } from '@coffeeplexity/ui';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Coffee, Eye, EyeOff } from 'lucide-react-native';
import { registerSchema, RegisterFormData } from '@coffeeplexity/shared';
import { UserRole } from '@coffeeplexity/shared';
import { useRegister } from '@coffeeplexity/api-client';
import Toast from 'react-native-toast-message';

// Utility functions for responsive design
const isTablet = (width: number): boolean => width >= 768;
const isLandscape = (width: number, height: number): boolean => width > height;

interface ResponsiveStyles {
  scrollContent: ViewStyle;
  header: ViewStyle;
  iconSize: number;
  title: TextStyle;
  subtitle: TextStyle;
  form: ViewStyle;
  nameRow: ViewStyle;
  inputContainer: ViewStyle;
  roleContainer: ViewStyle;
  checkboxContainer: ViewStyle;
  registerButton: ViewStyle;
  footer: ViewStyle;
  footerText: TextStyle;
}

export function RegisterScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const registerMutation = useRegister();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const windowDimensions = useWindowDimensions();

  // Responsive layout detection
  const isTabletDevice = isTablet(windowDimensions.width);
  const isLandscapeMode = isLandscape(
    windowDimensions.width,
    windowDimensions.height
  );
  const screenWidth = windowDimensions.width;
  const isSmallScreen = screenWidth < 375;
  const isLargeScreen = screenWidth > 768;

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      username: '',
      email: '',
      password: '',
      confirm_password: '',
      first_name: '',
      last_name: '',
      role: UserRole.USER,
      terms_accepted: false,
    },
    mode: 'onChange',
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await registerMutation.mutateAsync(data);
      Toast.show({
        type: 'success',
        text1: 'Registration Successful!',
        text2: 'Please check your email to verify your account.',
      });
    } catch (error: any) {
      let errorMessage = 'Please check your information and try again.';

      if (error.status === 409) {
        if (error.message.includes('email')) {
          setError('email', { message: 'This email is already registered' });
          errorMessage = 'This email is already registered';
        } else if (error.message.includes('username')) {
          setError('username', { message: 'This username is already taken' });
          errorMessage = 'This username is already taken';
        }
      } else if (error.status === 422) {
        errorMessage = 'Please check your input and try again';
      }

      Toast.show({
        type: 'error',
        text1: 'Registration Failed',
        text2: errorMessage,
      });
    }
  };

  // Responsive styles
  const responsiveStyles = getResponsiveStyles(
    isTabletDevice,
    isLandscapeMode,
    isSmallScreen,
    isLargeScreen
  );

  return (
    <SafeAreaView
      style={{
        ...styles.container,
        backgroundColor: theme.colors.background,
      }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView
          contentContainerStyle={{
            ...styles.scrollContent,
            ...responsiveStyles.scrollContent,
          }}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={{ ...styles.header, ...responsiveStyles.header }}>
            <Coffee
              size={responsiveStyles.iconSize}
              color={theme.colors.primary}
            />
            <Text
              variant={isLargeScreen ? 'displaySmall' : 'headlineLarge'}
              style={{
                ...styles.title,
                ...responsiveStyles.title,
                color: theme.colors.onBackground,
              }}
            >
              Create Account
            </Text>
            <Text
              variant={isSmallScreen ? 'bodyMedium' : 'bodyLarge'}
              style={{
                ...styles.subtitle,
                ...responsiveStyles.subtitle,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Join the coffee community
            </Text>
          </View>

          {/* Spacer */}
          <View style={{ height: 8 }} />

          {/* Form */}
          <View style={{ ...styles.form, ...responsiveStyles.form }}>
            {/* Name Fields */}
            <View style={{ ...styles.nameRow, ...responsiveStyles.nameRow }}>
              <Controller
                control={control}
                name='first_name'
                render={({ field: { onChange, onBlur, value } }) => (
                  <View style={[styles.inputContainer, styles.halfWidth]}>
                    <Input
                      label='First Name'
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.first_name?.message}
                    />
                  </View>
                )}
              />

              <Controller
                control={control}
                name='last_name'
                render={({ field: { onChange, onBlur, value } }) => (
                  <View style={[styles.inputContainer, styles.halfWidth]}>
                    <Input
                      label='Last Name'
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={errors.last_name?.message}
                    />
                  </View>
                )}
              />
            </View>

            <Controller
              control={control}
              name='username'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Username'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    autoCapitalize='none'
                    error={errors.username?.message}
                  />
                </View>
              )}
            />

            <Controller
              control={control}
              name='email'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Email'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    keyboardType='email-address'
                    autoCapitalize='none'
                    autoComplete='email'
                    error={errors.email?.message}
                  />
                </View>
              )}
            />

            <Controller
              control={control}
              name='password'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Password'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showPassword}
                    error={errors.password?.message}
                    rightIcon={
                      <Pressable onPress={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <EyeOff
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        ) : (
                          <Eye
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        )}
                      </Pressable>
                    }
                  />
                </View>
              )}
            />
            {/* Confirm Password Field */}
            <Controller
              control={control}
              name='confirm_password'
              render={({ field: { onChange, onBlur, value } }) => (
                <View
                  style={{
                    ...styles.inputContainer,
                    ...responsiveStyles.inputContainer,
                  }}
                >
                  <Input
                    label='Confirm Password'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showConfirmPassword}
                    error={errors.confirm_password?.message}
                    rightIcon={
                      <Pressable
                        onPress={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        ) : (
                          <Eye
                            size={20}
                            color={theme.colors.onSurfaceVariant}
                          />
                        )}
                      </Pressable>
                    }
                  />
                </View>
              )}
            />

            {/* Role Selection */}
            <View>
              <Text
                variant={isSmallScreen ? 'bodyMedium' : 'bodyLarge'}
                style={[
                  styles.roleLabel,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                I want to join as:
              </Text>
              <Controller
                control={control}
                name='role'
                render={({ field: { onChange, value } }) => (
                  <RadioButton.Group onValueChange={onChange} value={value}>
                    <View style={styles.roleOption}>
                      <RadioButton value={UserRole.USER} />
                      <View style={styles.roleTextContainer}>
                        <Text
                          style={[
                            styles.roleText,
                            { color: theme.colors.onSurface },
                          ]}
                        >
                          Coffee Enthusiast
                        </Text>
                        <Text
                          style={[
                            styles.roleDescription,
                            { color: theme.colors.onSurfaceVariant },
                          ]}
                        >
                          Discover and save coffee recipes
                        </Text>
                      </View>
                    </View>
                    <View style={styles.roleOption}>
                      <RadioButton value={UserRole.CREATOR} />
                      <View style={styles.roleTextContainer}>
                        <Text
                          style={[
                            styles.roleText,
                            { color: theme.colors.onSurface },
                          ]}
                        >
                          Content Creator
                        </Text>
                        <Text
                          style={[
                            styles.roleDescription,
                            { color: theme.colors.onSurfaceVariant },
                          ]}
                        >
                          Share your coffee recipes and earn
                        </Text>
                      </View>
                    </View>
                  </RadioButton.Group>
                )}
              />
            </View>

            {/* Terms Checkbox */}
            <Controller
              control={control}
              name='terms_accepted'
              render={({ field: { onChange, value } }) => (
                <View
                  style={{
                    ...styles.checkboxContainer,
                    ...responsiveStyles.checkboxContainer,
                  }}
                >
                  <Checkbox
                    status={value ? 'checked' : 'unchecked'}
                    onPress={() => onChange(!value)}
                  />
                  <Text
                    style={[
                      styles.termsText,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    I agree to the{' '}
                    <Text style={{ color: theme.colors.primary }}>
                      Terms of Service
                    </Text>{' '}
                    and{' '}
                    <Text style={{ color: theme.colors.primary }}>
                      Privacy Policy
                    </Text>
                  </Text>
                </View>
              )}
            />

            {/* Register Button */}
            <DSButton
              variant='primary'
              onPress={handleSubmit(onSubmit)}
              loading={registerMutation.isPending}
              disabled={registerMutation.isPending}
              style={{
                ...styles.registerButton,
                ...responsiveStyles.registerButton,
              }}
            >
              Create Account
            </DSButton>
          </View>

          {/* Sign In Link */}
          <View style={{ ...styles.footer, ...responsiveStyles.footer }}>
            <Text
              style={{
                ...styles.footerText,
                ...responsiveStyles.footerText,
                color: theme.colors.onSurfaceVariant,
              }}
            >
              Already have an account?{' '}
            </Text>
            <DSButton
              variant='ghost'
              onPress={() => navigation.navigate('login' as never)}
              style={styles.signInButton}
            >
              Sign In
            </DSButton>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

/**
 * Generates responsive styles based on device characteristics
 * Ensures consistent layout across mobile, tablet, and web platforms
 */
function getResponsiveStyles(
  isTablet: boolean,
  isLandscape: boolean,
  isSmallScreen: boolean,
  isLargeScreen: boolean
): ResponsiveStyles {
  const baseIconSize = 48;
  const tabletIconSize = 64;
  const landscapeIconSize = 40;

  return {
    scrollContent: {
      paddingHorizontal: isSmallScreen ? 16 : isLargeScreen ? 48 : 24,
      paddingVertical: isLandscape ? 12 : 24,
    },
    header: {
      marginTop: isLandscape ? 8 : 20,
      marginBottom: isLandscape ? 8 : 12,
    },
    iconSize: isTablet
      ? tabletIconSize
      : isLandscape
        ? landscapeIconSize
        : baseIconSize,
    title: {
      marginTop: isSmallScreen ? 8 : 12,
      fontSize: isLargeScreen ? 32 : isSmallScreen ? 24 : 28,
    },
    subtitle: {
      marginTop: isSmallScreen ? 4 : 8,
    },
    form: {
      flex: 1,
      maxWidth: isLargeScreen ? 500 : undefined,
      alignSelf: isLargeScreen ? 'center' : 'stretch',
      width: isLargeScreen ? '100%' : undefined,
    },
    nameRow: {
      gap: isSmallScreen ? 8 : 12,
    },
    inputContainer: {
      marginBottom: isSmallScreen ? 4 : 6,
    },
    roleContainer: {
      marginBottom: isSmallScreen ? 12 : 16,
    },
    checkboxContainer: {
      marginBottom: isSmallScreen ? 4 : 8,
    },
    registerButton: {
      marginTop: isSmallScreen ? 12 : 16,
      marginBottom: isSmallScreen ? 16 : 24,
    },
    footer: {
      flexDirection: isSmallScreen ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: isSmallScreen ? 4 : 0,
    },
    footerText: {
      fontSize: isSmallScreen ? 14 : 16,
    },
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  header: {
    alignItems: 'center',
  },

  title: {
    fontWeight: 'bold',
  },
  subtitle: {
    textAlign: 'center',
  },
  form: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
  },
  inputContainer: {},
  halfWidth: {
    flex: 1,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  termsText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 8,
  },
  roleContainer: {},
  roleLabel: {
    marginTop: 12,
    marginBottom: 12,
    fontWeight: '500',
  },
  roleOption: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  roleTextContainer: {
    flex: 1,
    marginLeft: 8,
  },
  roleText: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 2,
  },
  roleDescription: {
    fontSize: 14,
    lineHeight: 18,
  },
  registerButton: {},
  footer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {},
  signInButton: {
    marginLeft: 4,
  },
});
